import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Alert, LoadingButton } from '@mui/lab';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import Swal from 'sweetalert2';
import QuantityImportUtil from '../../../utils/QuantityImportUtil';
import CsvUtil from '../../../utils/CsvUtil';

// Tesseract pulls its worker, wasm core and language data over the network the
// first time it runs. The paths are overridable so they can be self hosted
// later without touching this component.
const tesseractOptions = () => {
  const options = {};
  if (window.TESSERACT_WORKER_PATH) {
    options.workerPath = window.TESSERACT_WORKER_PATH;
  }
  if (window.TESSERACT_CORE_PATH) {
    options.corePath = window.TESSERACT_CORE_PATH;
  }
  if (window.TESSERACT_LANG_PATH) {
    options.langPath = window.TESSERACT_LANG_PATH;
  }
  return options;
};

// Measured against the sample receiving sheets: every row the OCR pass got
// silently wrong scored below these, and almost every row it got right scored
// above them. Overridable so the cut can be retuned from env.js as more sheets
// go through.
const lineConfidenceFloor = () => (typeof window.QUANTITY_IMPORT_CONFIDENCE === 'number' ? window.QUANTITY_IMPORT_CONFIDENCE : 55);
const quantityConfidenceFloor = () => (typeof window.QUANTITY_IMPORT_QUANTITY_CONFIDENCE === 'number' ? window.QUANTITY_IMPORT_QUANTITY_CONFIDENCE : 30);

const isBelow = (value, floor) => typeof value === 'number' && value < floor;

/**
 * Whether a row is worth a second look before it moves stock.
 * @param {Object} row a review table row
 * @returns {boolean} true when either confidence score came back weak
 */
const needsReview = row => isBelow(row.confidence, lineConfidenceFloor()) || isBelow(row.quantity_confidence, quantityConfidenceFloor());

const statusChip = status => {
  switch (status) {
    case 'updated':
      return <Chip size="small" label="Updated" color="success" />;
    case 'not_found':
      return <Chip size="small" label="No Match" color="error" />;
    case 'odoo_not_found':
      return <Chip size="small" label="Not In Odoo" color="error" />;
    case 'missing_odoo_link':
      return <Chip size="small" label="Not Linked" color="warning" />;
    default:
      return <Chip size="small" label="Failed" color="error" />;
  }
};

const emptyEntry = { product_code: '', quantity: '' };

const QuantityImporterPage = props => {
  const { user, token, history, quantityImportCreate, quantityImportLookup, doCreateQuantityImport, doLookupQuantityImport, doQuantityImportReset } = props;

  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const previewUrlRef = useRef(null);
  const requestedRef = useRef({});

  const [previewUrl, setPreviewUrl] = useState(null);
  const [csvName, setCsvName] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanError, setScanError] = useState(null);
  const [lastScanCount, setLastScanCount] = useState(null);
  const [rows, setRows] = useState([]);
  const [entry, setEntry] = useState(emptyEntry);
  const [matches, setMatches] = useState({});

  useEffect(() => {
    doQuantityImportReset();
  }, []);

  // Object URLs for the preview have to be handed back or the blobs stay in
  // memory for the life of the session.
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  // A failed lookup releases its codes so the next edit retries instead of
  // leaving the matched product column blank with no explanation.
  useEffect(() => {
    if (!quantityImportLookup?.fetching && quantityImportLookup?.errors) {
      requestedRef.current = {};
    }
  }, [quantityImportLookup]);

  useEffect(() => {
    if (!quantityImportCreate?.fetching && quantityImportCreate?.errors) {
      Swal.fire({
        icon: 'error',
        text: 'The quantity import could not be applied. Please try again.',
      });
    }
  }, [quantityImportCreate]);

  // Resolved codes are kept so a code already looked up is never asked for
  // twice, which keeps the table from flickering while staff type.
  useEffect(() => {
    const results = quantityImportLookup?.data?.results;
    if (!Array.isArray(results) || results.length === 0) {
      return;
    }
    setMatches(current => {
      let changed = false;
      const next = { ...current };
      results.forEach(result => {
        if (result && result.product_code && next[result.product_code] !== result) {
          next[result.product_code] = result;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [quantityImportLookup]);

  // Resolve whatever codes are on screen but not yet known. Debounced so a code
  // being typed out character by character does not fire a request per keystroke.
  useEffect(() => {
    const codes = [];
    rows.forEach(row => {
      const code = (row.product_code || '').trim().toUpperCase();
      if (code.length >= 5 && !matches[code] && !requestedRef.current[code] && !codes.includes(code)) {
        codes.push(code);
      }
    });

    if (codes.length === 0) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const batch = codes.slice(0, 250);
      // Codes are marked as asked for before the call goes out, so a lookup
      // that answers with nothing for them cannot send the effect round again.
      batch.forEach(code => {
        requestedRef.current[code] = true;
      });
      doLookupQuantityImport({ token, payload: { codes: batch } });
    }, 600);

    return () => clearTimeout(timer);
  }, [rows, matches, token]);

  const setPreview = file => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  };

  const handleImageSelected = async event => {
    const file = event?.target?.files && event.target.files[0];
    // Clearing the input lets staff pick the very same file again after a retry.
    event.target.value = '';
    if (!file) {
      return;
    }

    doQuantityImportReset();
    setPreview(file);
    setCsvName(null);
    setScanError(null);
    setScanProgress(0);
    setScanning(true);
    setLastScanCount(null);

    let worker = null;
    try {
      // Loaded on demand so the ~2mb OCR bundle never lands on the pages the
      // rest of the catalog uses.
      const tesseractModule = await import('tesseract.js');
      const Tesseract = tesseractModule.createWorker ? tesseractModule : tesseractModule.default;
      worker = await Tesseract.createWorker({
        ...tesseractOptions(),
        logger: message => {
          if (message?.status === 'recognizing text' && typeof message?.progress === 'number') {
            setScanProgress(Math.round(message.progress * 100));
          }
        },
      });

      await worker.loadLanguage('eng');
      await worker.initialize('eng');

      const { data } = await worker.recognize(file);
      const parsed = QuantityImportUtil.parseOcrResult(data);

      // Scanning a second sheet adds to the list rather than replacing it, so a
      // two page delivery can be entered in one go.
      setRows(current =>
        current.concat(
          parsed.map(row => ({
            product_code: row.product_code,
            quantity: row.quantity === '' || row.quantity == null ? '' : String(row.quantity),
            confidence: row.confidence,
            quantity_confidence: row.quantity_confidence,
          })),
        ),
      );
      setLastScanCount(parsed.length);
    } catch (error) {
      setScanError('The image could not be read. Try a straighter, better lit photo, or type the rows in below.');
    } finally {
      if (worker) {
        try {
          await worker.terminate();
        } catch (error) {
          // A worker that already died on its own needs no further handling.
        }
      }
      setScanning(false);
      setScanProgress(0);
    }
  };

  const handleCsvSelected = event => {
    const file = event?.target?.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    doQuantityImportReset();
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setCsvName(file.name);
    setScanError(null);
    setLastScanCount(null);
    setScanning(true);

    const reader = new FileReader();

    reader.onerror = () => {
      setScanError('That file could not be read. Check it is a plain CSV export and try again.');
      setCsvName(null);
      setScanning(false);
    };

    reader.onload = () => {
      try {
        const parsed = QuantityImportUtil.parseCsvRows(CsvUtil.toGrid(String(reader.result || '')));

        // Same as a scan, rows are added to whatever is already listed so a
        // delivery split across files goes in as one import.
        setRows(current =>
          current.concat(
            parsed.map(row => ({
              product_code: row.product_code,
              quantity: row.quantity === '' || row.quantity == null ? '' : String(row.quantity),
              confidence: null,
              quantity_confidence: null,
            })),
          ),
        );
        setLastScanCount(parsed.length);
      } catch (error) {
        setScanError('That file could not be read as a CSV. Check the export and try again.');
      } finally {
        setScanning(false);
      }
    };

    reader.readAsText(file);
  };

  // Editing a field is a staff member vouching for it, so the matching warning
  // is retired. The code keeps its own warning until the code itself is touched.
  const handleRowChange = (index, field) => event => {
    const value = event.target.value;
    setRows(
      rows.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }
        const updated = { ...row, [field]: value };
        if (field === 'product_code') {
          updated.confidence = null;
        } else {
          updated.quantity_confidence = null;
        }
        return updated;
      }),
    );
  };

  const handleRemoveRow = index => {
    setRows(rows.filter((row, rowIndex) => rowIndex !== index));
  };

  const handleEntryChange = field => event => {
    setEntry({ ...entry, [field]: event.target.value });
  };

  const handleAddEntry = () => {
    const productCode = (entry.product_code || '').trim().toUpperCase();
    const quantity = parseInt(entry.quantity, 10);

    if (productCode === '' || Number.isNaN(quantity) || quantity <= 0) {
      Swal.fire({
        icon: 'error',
        text: 'Enter a product code and a quantity of at least 1 before adding the row.',
      });
      return;
    }

    setRows([...rows, { product_code: productCode, quantity: String(quantity), confidence: null, quantity_confidence: null }]);
    setEntry(emptyEntry);
  };

  const handleEntryKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddEntry();
    }
  };

  const handleStartOver = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    requestedRef.current = {};
    setPreviewUrl(null);
    setCsvName(null);
    setRows([]);
    setEntry(emptyEntry);
    setLastScanCount(null);
    setScanError(null);
    doQuantityImportReset();
  };

  const results = quantityImportCreate?.data?.results;
  const unappliedResults = Array.isArray(results) ? results.filter(result => result?.status !== 'updated') : [];

  // Only the rows that did not land come back for another pass. Returning an
  // already applied row would add its quantity to Odoo a second time.
  const handleReviseUnapplied = () => {
    setRows(
      unappliedResults.map(result => ({
        product_code: result.product_code,
        quantity: result.quantity == null ? '' : String(result.quantity),
        confidence: null,
        quantity_confidence: null,
      })),
    );
    setEntry(emptyEntry);
    doQuantityImportReset();
  };

  const validRows = rows
    .map(row => ({ product_code: (row.product_code || '').trim().toUpperCase(), quantity: parseInt(row.quantity, 10) }))
    .filter(row => row.product_code !== '' && !Number.isNaN(row.quantity) && row.quantity > 0);

  const invalidRowCount = rows.length - validRows.length;
  const lookupUnavailable = Boolean(quantityImportLookup?.errors);
  const flaggedRowCount = rows.filter(needsReview).length;
  const unmatchedCodes = validRows.filter(row => matches[row.product_code] && !matches[row.product_code].found).map(row => row.product_code);

  const handleConfirm = () => {
    if (validRows.length === 0) {
      Swal.fire({
        icon: 'error',
        text: 'There is nothing to import. Every row needs a product code and a quantity of at least 1.',
      });
      return;
    }

    const warnings = [];
    if (unmatchedCodes.length >= 1) {
      warnings.push(`<p style="color:#d33"><b>${unmatchedCodes.length} code${unmatchedCodes.length === 1 ? '' : 's'} match no product and will fail:</b><br/>${unmatchedCodes.join(', ')}</p>`);
    }
    if (flaggedRowCount >= 1) {
      warnings.push(`<p style="color:#d33"><b>${flaggedRowCount} row${flaggedRowCount === 1 ? '' : 's'} still flagged as a poor read.</b></p>`);
    }

    Swal.fire({
      icon: 'warning',
      title: 'Apply these quantities?',
      html: `<p>${validRows.length} product${validRows.length === 1 ? '' : 's'} will have the entered amount added to the on hand quantity in Odoo.</p>${warnings.join('')}`,
      showCancelButton: true,
      confirmButtonText: 'Yes, update Odoo',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }
      doCreateQuantityImport({ token, payload: { items: validRows } });
    });
  };

  if (!user || !user?.role || user?.role?.name !== 'admin') {
    return <Alert severity="error">Permission Denied</Alert>;
  }

  const matchCell = row => {
    const code = (row.product_code || '').trim().toUpperCase();
    if (code === '') {
      return <span style={{ color: '#999' }}>-</span>;
    }
    const match = matches[code];
    if (!match) {
      return quantityImportLookup?.fetching ? <CircularProgress size={14} /> : <span style={{ color: '#999' }}>-</span>;
    }
    if (!match.found) {
      return <Chip size="small" label="No matching product" color="error" />;
    }
    return (
      <>
        <p style={{ margin: 0 }}>{match.product_name}</p>
        <p style={{ margin: 0, fontSize: 12, color: '#666' }}>{match.variant_name}</p>
        {match.linked ? '' : <Chip size="small" label="Not linked to Odoo" color="warning" />}
      </>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12} lg={12}>
        <Typography align="left" variant="h4" sx={{ fontWeight: 'bold', textAlign: `center` }}>
          Quantity Importer
        </Typography>
      </Grid>

      <Grid item xs={12} md={12} lg={12}>
        <Alert severity="info">
          Photograph the receiving sheet, pick an image from the device, or load a CSV export, then check every row before applying. Quantities are added on top of what Odoo already has on hand. Rows
          can be edited, removed, or typed in by hand at the bottom of the table.
        </Alert>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <LoadingButton
          loadingIndicator={<CircularProgress style={{ color: `#fff` }} color="inherit" size={16} />}
          onClick={() => {
            history.push('/catalog/automation');
          }}
          size={`large`}
          variant="contained"
          color="primary"
          fullWidth={true}
          sx={{ textTransform: 'none' }}
        >
          Go Back
        </LoadingButton>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleImageSelected} style={{ display: 'none' }} />
        <LoadingButton
          loadingIndicator={<CircularProgress style={{ color: `#fff` }} color="inherit" size={16} />}
          onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
          loading={scanning}
          disabled={scanning || quantityImportCreate?.fetching}
          startIcon={<PhotoCameraIcon />}
          size={`large`}
          variant="contained"
          color="primary"
          fullWidth={true}
          sx={{ textTransform: 'none' }}
        >
          Take Photo
        </LoadingButton>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <input ref={libraryInputRef} type="file" accept="image/*" onChange={handleImageSelected} style={{ display: 'none' }} />
        <LoadingButton
          loadingIndicator={<CircularProgress style={{ color: `#fff` }} color="inherit" size={16} />}
          onClick={() => libraryInputRef.current && libraryInputRef.current.click()}
          loading={scanning}
          disabled={scanning || quantityImportCreate?.fetching}
          startIcon={<PhotoLibraryIcon />}
          size={`large`}
          variant="contained"
          color="primary"
          fullWidth={true}
          sx={{ textTransform: 'none' }}
        >
          Select Image
        </LoadingButton>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <input ref={csvInputRef} type="file" accept=".csv,text/csv,text/plain,application/vnd.ms-excel" onChange={handleCsvSelected} style={{ display: 'none' }} />
        <LoadingButton
          loadingIndicator={<CircularProgress style={{ color: `#fff` }} color="inherit" size={16} />}
          onClick={() => csvInputRef.current && csvInputRef.current.click()}
          loading={scanning}
          disabled={scanning || quantityImportCreate?.fetching}
          startIcon={<DescriptionOutlinedIcon />}
          size={`large`}
          variant="contained"
          color="primary"
          fullWidth={true}
          sx={{ textTransform: 'none' }}
        >
          Select CSV
        </LoadingButton>
      </Grid>

      {previewUrl && !results ? (
        <Grid item xs={12} md={12} lg={12}>
          <img src={previewUrl} alt="Selected receiving sheet" style={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain' }} />
        </Grid>
      ) : (
        ''
      )}

      {csvName && !results ? (
        <Grid item xs={12} md={12} lg={12}>
          <Chip icon={<DescriptionOutlinedIcon />} label={csvName} />
        </Grid>
      ) : (
        ''
      )}

      {scanning ? (
        <Grid item xs={12} md={12} lg={12}>
          <Typography variant="body2" style={{ marginBottom: 6 }}>
            {csvName ? 'Reading the file...' : `Reading the sheet... ${scanProgress}%`}
          </Typography>
          <LinearProgress variant={scanProgress > 0 ? 'determinate' : 'indeterminate'} value={scanProgress} />
        </Grid>
      ) : (
        ''
      )}

      {scanError ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="error">{scanError}</Alert>
        </Grid>
      ) : (
        ''
      )}

      {lastScanCount === 0 && !scanning && !scanError ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="warning">No product codes were found in that file. Try another photo or export, or type the rows in below.</Alert>
        </Grid>
      ) : (
        ''
      )}

      {results ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity={quantityImportCreate?.data?.failed >= 1 ? 'warning' : 'success'}>
            {quantityImportCreate?.data?.updated} product{quantityImportCreate?.data?.updated === 1 ? '' : 's'} updated
            {quantityImportCreate?.data?.failed >= 1 ? `, ${quantityImportCreate?.data?.failed} could not be applied` : ''}.
          </Alert>
        </Grid>
      ) : (
        ''
      )}

      {results ? (
        <Grid item xs={12} md={12} lg={12}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>PRODUCT_CODE</TableCell>
                  <TableCell>PRODUCT</TableCell>
                  <TableCell style={{ textAlign: `center` }}>ADDED</TableCell>
                  <TableCell style={{ textAlign: `center` }}>BEFORE</TableCell>
                  <TableCell style={{ textAlign: `center` }}>AFTER</TableCell>
                  <TableCell style={{ textAlign: `center` }}>STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map(result => (
                  <TableRow key={`result-${result?.product_code}`}>
                    <TableCell>{result?.product_code}</TableCell>
                    <TableCell>
                      <p style={{ margin: 0 }}>{result?.product_name}</p>
                      <p style={{ margin: 0, fontSize: 12 }}>{result?.variant_name}</p>
                    </TableCell>
                    <TableCell style={{ textAlign: `center` }}>{result?.quantity}</TableCell>
                    <TableCell style={{ textAlign: `center` }}>{result?.previous_quantity != null ? result?.previous_quantity : '-'}</TableCell>
                    <TableCell style={{ textAlign: `center` }}>{result?.new_quantity != null ? result?.new_quantity : '-'}</TableCell>
                    <TableCell style={{ textAlign: `center` }}>{statusChip(result?.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      ) : (
        ''
      )}

      {results && unappliedResults.length >= 1 ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="info">
            Revising brings back only the {unappliedResults.length} row{unappliedResults.length === 1 ? '' : 's'} that did not go through. The rows that did are left out on purpose, so nothing gets
            added to Odoo twice.
          </Alert>
        </Grid>
      ) : (
        ''
      )}

      {results && unappliedResults.length >= 1 ? (
        <Grid item xs={12} sm={6}>
          <LoadingButton onClick={handleReviseUnapplied} size={`large`} variant="contained" color="primary" fullWidth={true} sx={{ textTransform: 'none' }}>
            {`Revise ${unappliedResults.length} Unapplied Row${unappliedResults.length === 1 ? '' : 's'}`}
          </LoadingButton>
        </Grid>
      ) : (
        ''
      )}

      {results ? (
        <Grid item xs={12} sm={unappliedResults.length >= 1 ? 6 : 12}>
          <LoadingButton onClick={handleStartOver} size={`large`} variant="contained" color="primary" fullWidth={true} sx={{ textTransform: 'none' }}>
            Start A New Sheet
          </LoadingButton>
        </Grid>
      ) : (
        ''
      )}

      {!results ? (
        <Grid item xs={12} md={12} lg={12}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: 170 }}>PRODUCT_CODE</TableCell>
                  <TableCell style={{ minWidth: 90 }}>QUANTITY</TableCell>
                  <TableCell style={{ minWidth: 160 }}>MATCHED PRODUCT</TableCell>
                  <TableCell style={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`quantity-import-row-${index}`} style={needsReview(row) ? { backgroundColor: '#fff6e5' } : {}}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {needsReview(row) ? (
                          <Tooltip title="The scan was unsure about this row. Check it against the sheet.">
                            <ReportProblemOutlinedIcon color="warning" fontSize="small" style={{ marginRight: 6 }} />
                          </Tooltip>
                        ) : (
                          ''
                        )}
                        <TextField
                          id={`product_code_${index}`}
                          type="text"
                          value={row.product_code}
                          onChange={handleRowChange(index, 'product_code')}
                          disabled={quantityImportCreate?.fetching}
                          error={Boolean(matches[(row.product_code || '').trim().toUpperCase()] && !matches[(row.product_code || '').trim().toUpperCase()].found)}
                          size="small"
                          fullWidth={true}
                          inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false }}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <TextField
                        id={`quantity_${index}`}
                        type="number"
                        value={row.quantity}
                        onChange={handleRowChange(index, 'quantity')}
                        disabled={quantityImportCreate?.fetching}
                        error={row.quantity === ''}
                        size="small"
                        fullWidth={true}
                        inputProps={{ min: 1, inputMode: 'numeric' }}
                      />
                    </TableCell>
                    <TableCell>{matchCell(row)}</TableCell>
                    <TableCell style={{ textAlign: `center` }}>
                      <IconButton onClick={() => handleRemoveRow(index)} disabled={quantityImportCreate?.fetching} size="small">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <TextField
                      id="entry_product_code"
                      label="Add product code"
                      type="text"
                      value={entry.product_code}
                      onChange={handleEntryChange('product_code')}
                      onKeyPress={handleEntryKeyPress}
                      disabled={quantityImportCreate?.fetching}
                      size="small"
                      fullWidth={true}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false, placeholder: 'ALP-01-0000' }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      id="entry_quantity"
                      label="Quantity"
                      type="number"
                      value={entry.quantity}
                      onChange={handleEntryChange('quantity')}
                      onKeyPress={handleEntryKeyPress}
                      disabled={quantityImportCreate?.fetching}
                      size="small"
                      fullWidth={true}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 1, inputMode: 'numeric' }}
                    />
                  </TableCell>
                  <TableCell colSpan={2}>
                    <LoadingButton
                      onClick={handleAddEntry}
                      disabled={quantityImportCreate?.fetching}
                      startIcon={<AddIcon />}
                      variant="contained"
                      color="primary"
                      fullWidth={true}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Row
                    </LoadingButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      ) : (
        ''
      )}

      {!results && flaggedRowCount >= 1 ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="warning">
            {flaggedRowCount} row{flaggedRowCount === 1 ? '' : 's'} highlighted below came back from the scan with low confidence. Check {flaggedRowCount === 1 ? 'it' : 'them'} against the sheet - the
            warning clears once the field is edited.
          </Alert>
        </Grid>
      ) : (
        ''
      )}

      {!results && lookupUnavailable ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="warning">Product names could not be looked up, so the matched product column is empty. The rows can still be applied, but nothing is confirming the codes are right.</Alert>
        </Grid>
      ) : (
        ''
      )}

      {!results && unmatchedCodes.length >= 1 ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="error">
            {unmatchedCodes.length} code{unmatchedCodes.length === 1 ? '' : 's'} match no product and will fail if applied: {unmatchedCodes.join(', ')}
          </Alert>
        </Grid>
      ) : (
        ''
      )}

      {!results && invalidRowCount >= 1 ? (
        <Grid item xs={12} md={12} lg={12}>
          <Alert severity="warning">
            {invalidRowCount} row{invalidRowCount === 1 ? '' : 's'} will be skipped - every row needs a product code and a quantity of at least 1.
          </Alert>
        </Grid>
      ) : (
        ''
      )}

      {!results && rows.length >= 1 ? (
        <Grid item xs={12} md={12} lg={12}>
          <LoadingButton
            loadingIndicator={<CircularProgress style={{ color: `#fff` }} color="inherit" size={16} />}
            onClick={handleConfirm}
            loading={quantityImportCreate?.fetching}
            disabled={scanning || quantityImportCreate?.fetching || validRows.length === 0}
            size={`large`}
            variant="contained"
            color="primary"
            fullWidth={true}
            sx={{ textTransform: 'none' }}
          >
            {`Verified - Update ${validRows.length} Product${validRows.length === 1 ? '' : 's'}`}
          </LoadingButton>
        </Grid>
      ) : (
        ''
      )}
    </Grid>
  );
};

QuantityImporterPage.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  token: PropTypes.string,
  quantityImportCreate: PropTypes.object,
  quantityImportLookup: PropTypes.object,
  doCreateQuantityImport: PropTypes.func,
  doLookupQuantityImport: PropTypes.func,
  doQuantityImportReset: PropTypes.func,
};

export default QuantityImporterPage;
